import {useEffect} from 'react'
import {useWatch, type UseFormReturn} from 'react-hook-form'
import type {LeadBasicInputs} from 'components/leads/schemas/useLeadBasicSchema'
import {lookupSwissCanton} from 'services/swissCantonLookup'

type CantonAutofillOptions = Pick<UseFormReturn<LeadBasicInputs>, 'control' | 'getValues' | 'setValue'> & {
    countryCode: string
    addressId: number | null
}

export const useLeadCantonAutofill = ({control, getValues, setValue, countryCode, addressId}: CantonAutofillOptions) => {
    const fields = ['street_address', 'postal_code', 'city', 'state', 'country'] as const
    const [streetAddress, postalCode, city, state, country] = useWatch({control, name: fields})

    useEffect(() => {
        if (countryCode !== 'CH' || state?.trim() || !city?.trim()
            || (postalCode?.trim() ? !/^\d{4}$/.test(postalCode.trim()) : !streetAddress?.trim())) return

        const snapshot = [streetAddress, postalCode, city, state, country]
        let active = true
        const timer = setTimeout(() => {
            void lookupSwissCanton({streetAddress, postalCode, city}).then((canton) => {
                // A late response must not overwrite a manual selection or another address.
                const current = getValues(['street_address', 'postal_code', 'city', 'state', 'country'])
                if (!active || !canton || current.some((value, index) => value !== snapshot[index])) return
                setValue('state', canton, {shouldDirty: true, shouldValidate: true})
            }).catch(() => {})
        }, 500)
        return () => {
            active = false
            clearTimeout(timer)
        }
    }, [streetAddress, postalCode, city, state, country, countryCode, addressId, getValues, setValue])
}
